/* events-calendar.js
 * Pop-Up Fairway events calendar.
 *
 * Reads inline JSON for public events (rendered by the Liquid section) and
 * fetches a sanitized bookings JSON for private blocks. Renders a month grid;
 * public events win on time overlap.
 *
 * No dependencies, no build step. Drop into theme assets as-is.
 */
(function () {
  'use strict';

  // ---- one-time wiring ---------------------------------------------------
  const root = document.querySelector('[data-puf-events]');
  if (!root) return;

  const gridEl = root.querySelector('[data-calendar-root]');
  const monthLabelEl = root.querySelector('[data-month-label]');
  const modal = root.querySelector('[data-modal]');
  const modalBody = root.querySelector('[data-modal-body]');

  const bookingsUrl = root.dataset.bookingsUrl;
  const defaultBookingUrl = {
    large: root.dataset.defaultLarge || '/',
    standard: root.dataset.defaultStandard || '/',
  };
  const showPast = root.dataset.showPast === 'true';

  // Anchor everything to America/Chicago wall-clock; that's the sim's tz and
  // what the iCal feed emits. Visitors in other timezones see Chicago times
  // labeled as such, which is what they want to know.
  const TZ = 'America/Chicago';

  // ---- parsing -----------------------------------------------------------

  function parseEventTime(value) {
    if (!value) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-').map(Number);
      return { date: { y, m, d }, time: null, allDay: true };
    }
    const tzSplit = value.split('|');
    const isoPart = tzSplit[0];
    const match = isoPart.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(Z)?$/
    );
    if (!match) return null;
    const [, y, m, d, hh, mm, ss, z] = match;
    return {
      date: { y: +y, m: +m, d: +d },
      time: { h: +hh, m: +mm, s: ss ? +ss : 0 },
      allDay: false,
      utc: !!z,
    };
  }

  function daysInMonth(y, m) {
    return new Date(y, m, 0).getDate();
  }

  function firstWeekday(y, m) {
    return new Date(y, m - 1, 1).getDay();
  }

  function cmpTimes(a, b) {
    if (!a || !b) return 0;
    const ka = a.date.y * 10000 + a.date.m * 100 + a.date.d;
    const kb = b.date.y * 10000 + b.date.m * 100 + b.date.d;
    if (ka !== kb) return ka < kb ? -1 : 1;
    const tA = a.time ? a.time.h * 3600 + a.time.m * 60 : 0;
    const tB = b.time ? b.time.h * 3600 + b.time.m * 60 : 0;
    return tA < tB ? -1 : tA > tB ? 1 : 0;
  }

  function rangeCoversDay(start, end, y, m, d) {
    if (!start) return false;
    const dayKey = y * 10000 + m * 100 + d;
    const startKey = start.date.y * 10000 + start.date.m * 100 + start.date.d;
    if (!end) return dayKey === startKey;
    const endKey = end.date.y * 10000 + end.date.m * 100 + end.date.d;
    return dayKey >= startKey && dayKey <= endKey;
  }

  function rangesOverlap(s1, e1, s2, e2) {
    return cmpTimes(s1, e2 || s2) <= 0 && cmpTimes(s2, e1 || s1) <= 0;
  }

function formatTimeRangeCompact(start, end) {
    if (!start) return '';
    if (start.allDay) return 'All day';
    const fmt = (t) => {
      if (!t.time) return '';
      let h = t.time.h;
      const ampm = h >= 12 ? 'pm' : 'am';
      h = h % 12 || 12;
      if (t.time.m === 0) return `${h}${ampm}`;
      return `${h}:${String(t.time.m).padStart(2, '0')}${ampm}`;
    };
    if (!end) return fmt(start);
    return `${fmt(start)}-${fmt(end)}`;
  }

  function formatTimeRange(start, end) {
    if (!start) return '';
    if (start.allDay) return 'All day';
    const fmt = (t) => {
      if (!t.time) return '';
      let h = t.time.h;
      const ampm = h >= 12 ? 'pm' : 'am';
      h = h % 12 || 12;
      const mm = String(t.time.m).padStart(2, '0');
      return `${h}:${mm} ${ampm}`;
    };
    if (!end) return fmt(start);
    if (
      start.date.y === end.date.y &&
      start.date.m === end.date.m &&
      start.date.d === end.date.d
    ) {
      return `${fmt(start)} – ${fmt(end)}`;
    }
    return `${fmt(start)} – ${fmt(end)} (next day)`;
  }

  function formatLongDate(t) {
    if (!t) return '';
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    return `${months[t.date.m - 1]} ${t.date.d}, ${t.date.y}`;
  }

  // ---- data load ---------------------------------------------------------

  function loadPublicEvents() {
    const tag = root.querySelector('script[data-public-events]');
    if (!tag) return [];
    try {
      const raw = JSON.parse(tag.textContent || '[]');
      return raw
        .map((e) => ({
          ...e,
          _start: parseEventTime(e.start_time),
          _end: parseEventTime(e.end_time),
        }))
        .filter((e) => e._start);
    } catch (err) {
      console.warn('Events calendar: failed to parse public events', err);
      return [];
    }
  }

  async function loadBookings() {
    if (!bookingsUrl) return [];
    try {
      const resp = await fetch(bookingsUrl, { cache: 'no-cache' });
      if (!resp.ok) return [];
      const data = await resp.json();
      const list = Array.isArray(data) ? data : data.bookings || [];
      return list
        .map((b) => ({
          ...b,
          _start: parseEventTime(b.start),
          _end: parseEventTime(b.end),
        }))
        .filter((b) => b._start);
    } catch (err) {
      console.warn('Events calendar: failed to load bookings', err);
      return [];
    }
  }

  // ---- rendering ---------------------------------------------------------

  function dayCellHTML(dayNum, classes, contents) {
    return (
      `<div class="puf-day ${classes.join(' ')}" role="gridcell">` +
      `<div class="puf-day-num">${dayNum}</div>` +
      contents +
      `</div>`
    );
  }

  function renderMonth(year, month, publicEvents, bookings) {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    monthLabelEl.textContent = `${monthNames[month - 1]} ${year}`;

    const dim = daysInMonth(year, month);
    const lead = firstWeekday(year, month);

    const now = new Date();
    const todayKey = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;

    const dayHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      .map((d) => `<div class="puf-dow">${d}</div>`)
      .join('');

    const cells = [];
    for (let i = 0; i < lead; i++) {
      cells.push('<div class="puf-day puf-day-empty" role="gridcell" aria-hidden="true"></div>');
    }
    for (let d = 1; d <= dim; d++) {
      const isToday = todayKey === `${year}-${month}-${d}`;
      const isPast = new Date(year, month - 1, d) < new Date(now.getFullYear(), now.getMonth(), now.getDate());

      const publics = publicEvents.filter((e) =>
        rangeCoversDay(e._start, e._end, year, month, d)
      );
      const bookingsForDay = bookings.filter((b) => {
        if (!rangeCoversDay(b._start, b._end, year, month, d)) return false;
        const hidden = publics.some(
          (p) => p.sim === b.sim && rangesOverlap(p._start, p._end, b._start, b._end)
        );
        return !hidden;
      });

      let inner = '';
      if (publics.length > 0) {
        const e = publics[0];
        const bgStyle = e.venue_photo
          ? `style="background-image:url('${e.venue_photo.replace(/'/g, "\\'")}')"`
          : '';
        inner += `<button type="button" class="puf-day-public" data-event-id="${e.id}" ${bgStyle} aria-label="View event: ${escapeHTML(e.name)}">`;
        inner += `  <span class="puf-day-public-scrim"></span>`;
        inner += `  <span class="puf-day-public-venue">${escapeHTML(e.name)}</span>`;
        inner += `  <span class="puf-day-public-time">${formatTimeRange(e._start, e._end)}</span>`;
        inner += `</button>`;
        if (publics.length > 1) {
          inner += `<div class="puf-day-more">+${publics.length - 1} more</div>`;
        }
      }
      if (bookingsForDay.length > 0) {
        bookingsForDay.forEach((b) => {
          const timeRange = formatTimeRangeCompact(b._start, b._end);
          inner += `<div class="puf-day-booked">Private Event<span class="puf-day-booked-time">${escapeHTML(timeRange)}</span></div>`;
        });
      }

      const classes = [];
      if (isToday) classes.push('puf-day-today');
      if (isPast && !showPast && publics.length === 0) classes.push('puf-day-past');
      if (publics.length > 0) classes.push('puf-day-has-public');
      if (bookingsForDay.length > 0) classes.push('puf-day-has-booking');

      cells.push(dayCellHTML(d, classes, inner));
    }

    root._publicEventsById = {};
    publicEvents.forEach((e) => (root._publicEventsById[e.id] = e));

    gridEl.innerHTML = `
      <div class="puf-dow-row">${dayHeaders}</div>
      <div class="puf-days-row">${cells.join('')}</div>
    `;
  }

  function escapeHTML(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  function escapeAttr(s) {
    return escapeHTML(s).replace(/[^A-Za-z0-9_-]/g, '');
  }

  // ---- modal -------------------------------------------------------------

  function openModalForEvent(eventId) {
    const e = root._publicEventsById && root._publicEventsById[eventId];
    if (!e) return;

    const bookingUrl =
      e.booking_url || defaultBookingUrl[e.sim] || defaultBookingUrl.large;

    const mapSrc = e.address
      ? `https://www.google.com/maps?q=${encodeURIComponent(e.address)}&output=embed`
      : '';

    modalBody.innerHTML = `
      ${e.venue_photo
        ? `<img class="puf-modal-photo" src="${escapeHTML(e.venue_photo)}" alt="${escapeHTML(e.venue_name)}" />`
        : ''}
      <h2 id="puf-modal-title" class="puf-modal-title">${escapeHTML(e.name)}</h2>
      <div class="puf-modal-venue">${escapeHTML(e.venue_name)}</div>
      <div class="puf-modal-when">
        ${escapeHTML(formatLongDate(e._start))} &middot;
        ${escapeHTML(formatTimeRange(e._start, e._end))}
        <span class="puf-modal-tz">(${TZ.replace('America/', '').replace('_', ' ')} time)</span>
      </div>
      ${e.address
        ? `<div class="puf-modal-address">${escapeHTML(e.address)}</div>`
        : ''}
      ${mapSrc
        ? `<iframe class="puf-modal-map" src="${mapSrc}" loading="lazy" allowfullscreen referrerpolicy="no-referrer-when-downgrade" title="Map of ${escapeHTML(e.venue_name)}"></iframe>`
        : ''}
      <div class="puf-modal-actions">
        <a class="puf-modal-cta puf-modal-cta-primary" href="${escapeHTML(bookingUrl)}">Book A Pop-Up</a>
        ${e.venue_url
          ? `<a class="puf-modal-cta puf-modal-cta-secondary" href="${escapeHTML(e.venue_url)}" target="_blank" rel="noopener">Visit ${escapeHTML(e.venue_name)}</a>`
          : ''}
      </div>
    `;
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
  }

  function closeModal() {
    modal.hidden = true;
    document.body.style.overflow = '';
  }

  // ---- boot --------------------------------------------------------------

  let state = {
    year: 0,
    month: 0,
    publicEvents: [],
    bookings: [],
  };

  function advanceMonth(delta) {
    let m = state.month + delta;
    let y = state.year;
    while (m < 1) { m += 12; y -= 1; }
    while (m > 12) { m -= 12; y += 1; }
    state.year = y;
    state.month = m;
    renderMonth(y, m, state.publicEvents, state.bookings);
  }

  root.addEventListener('click', (ev) => {
    const navBtn = ev.target.closest('[data-nav]');
    if (navBtn) {
      advanceMonth(navBtn.dataset.nav === 'next' ? 1 : -1);
      return;
    }
    const publicBtn = ev.target.closest('[data-event-id]');
    if (publicBtn) {
      openModalForEvent(publicBtn.dataset.eventId);
      return;
    }
    if (ev.target.closest('[data-modal-close]')) {
      closeModal();
      return;
    }
  });

  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && !modal.hidden) closeModal();
  });

  (async function init() {
    const now = new Date();
    state.year = now.getFullYear();
    state.month = now.getMonth() + 1;
    state.publicEvents = loadPublicEvents();
    state.bookings = await loadBookings();
    renderMonth(state.year, state.month, state.publicEvents, state.bookings);
  })();
})();